/* eslint-disable react-hooks/exhaustive-deps */
import {
  useState,
  useContext,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useModal } from "../../../context/ModalContext";
import { ThemeContext } from "../../../context/ThemeContext";
import MainHeader from "../../../components/header/MainHeader";
import AddressSearchPreview from "../AddressSearchPreview";
import { formatCurrency } from "../../../utils/format";
import { useCurrencyFormat } from "./hooks/useCurrencyFormat";
import FloatingInput from "../../../components/input/FloatingInput";
import FloatingNumber from "../../../components/input/FloatingNumber";
import CustomSelect from "../../../components/customselect/CustomSelect";

import ResultsModal from "./components/ResultsModal";
import ManualSheetSection from "./components/ManualSheetSection";
import PropertyDetailsSection from "./components/PropertyDetailsSection";
import { useUserTemplate } from "./hooks/useUserTemplate";
import { useBuyerTemplateData } from "./hooks/useBuyerTemplateData";
import {
  getTitleEscrowCosts,
  getStateSettings,
  getNationalSettings,
  getCountySettings,
} from "./services/titleEscrowService";
import {
  getPrepaidPaymentData,
  getAmountConventional,
  getBuyerEstimatedTax,
  computeClosingCostTotals,
  calculateDiscount,
  getDaysInterestAmount,
  getMonthlyPMIAmount,
  getAnnualTaxforPrepaid,
  computeTotals,
  setPrepaidData,
  resultOfTitleEscrow,
  getPrepaidMonthTaxes,
  getMonthInsurance,
  getTwoMonthPMI,
  getPSecondTd,
  getAmountFHA,
  getAmountVA,
} from "./utils/calculations";
import { applyWaSalesTaxClosingCosts } from "../buyer/utils/waSalesTaxClosingCosts";
import { withOriginationFeeConverted } from "../../../utils/originationFee";
import {
  hasAutoEstimatableLoanBalance,
  getAutoEstimatedLoanBalance,
  getAutoEstimatedLoanRate,
} from "../../../utils/autoEstimateLoan";

import "../../../calculater.css";
import "../../../responsiveness.css";
import { CommonHeader } from "../../../components/ui/CommonHeader";
import {
  saveRefinanceCalculator,
  updateRefinanceCalculator,
  getRefinanceCalc,
} from "./services/refinanceNetService";
import Toast from "../../../components/common/Toast";
import { buildBuyerPayload } from "../../../utils/payloadBuilder";
import { generatePdf } from "../PdfRouteService";

const RefinanceNetSheet = () => {
  const theme = useContext(ThemeContext);
  const mainId = theme?.id || "";
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const userId = user?.id;
  const { openModal, closeModal } = useModal();

  const [companyId, setCompanyId] = useState(user?.companyid || null);
  const main_company_name = theme?.main_company_name || null;

  const params = useParams();
  const calcId = params?.id || 0;
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isEditLoading, setIsEditLoading] = useState(false);

  const [formData, setFormData] = useState({});
  const latestFormDataRef = useRef(formData);
  useEffect(() => {
    latestFormDataRef.current = formData;
  }, [formData]);

  const defaultValues = useMemo(() => ({ ...formData }), [formData]);
  const { control, setValue, getValues } = useForm({ defaultValues });

  const [templateData, setTemplateData] = useState(null);

  const hookTemplate = useBuyerTemplateData(mainId, userId, companyId);

  useEffect(() => {
    if (hookTemplate?.buyerSections) {
      setTemplateData((prev) => ({
        ...prev,
        buyerSections: hookTemplate.buyerSections,
      }));
    }
  }, [hookTemplate]);

  const [ttbData, setTtbData] = useState({});
  const salesPriceError = "";
  const mortgageAmountError = "";
  const [isCorrectProperty, setIsCorrectProperty] = useState(true);
  const [showPreviewFromChild, setShowPreviewFromChild] = useState(false);
  const [useMortgageBalance, setUseMortgageBalance] = useState(true);
  const [useAnnualPropertyTax, setUseAnnualPropertyTax] = useState(true);
  const [useHomeWarranty, setUseHomeWarranty] = useState(false);
  const [showManualSheet, setShowManualSheet] = useState(false);
  const [toast, setToast] = useState({
    message: "",
    visible: false,
    type: "success",
  });

  const logFormAfterTemplateOnceRef = useRef(false);
  const logFormAfterTemplate = useCallback((nextForm) => {
    if (logFormAfterTemplateOnceRef.current) return;
    logFormAfterTemplateOnceRef.current = true;
    console.log("[refinance] form after user template apply", nextForm);
  }, []);

  useUserTemplate(
    mainId,
    userId,
    companyId,
    setFormData,
    hookTemplate,
    logFormAfterTemplate,
  );

  const handleCurrencyBlur = useCurrencyFormat(formData, setFormData);

  const parseCurrency = (val) => {
    const n = Number(String(val ?? "").replace(/[^0-9.-]+/g, ""));
    return Number.isFinite(n) ? n : 0;
  };

  const handleClosingCostUpdate = (camelKey, field, value) => {
    setFormData((prev) => {
      const updatedList = prev.closingCostsMeta.map((item) => {
        if (item.camelKey !== camelKey) return item;
        return { ...item, [field]: value, isManual: true };
      });
      return computeClosingCostTotals({
        ...prev,
        closingCostsMeta: updatedList,
      });
    });
  };

  const preserveAddressFields = (prev = {}, updated = {}) => {
    const result = { ...updated };
    if (updated.addressVerified === undefined) {
      result.addressVerified = prev.addressVerified ?? false;
    }
    result.addressMeta = {
      ...(prev.addressMeta || {}),
      ...(updated.addressMeta || {}),
    };
    result.loanInfoMeta = {
      ...(prev.loanInfoMeta || {}),
      ...(updated.loanInfoMeta || {}),
    };
    return result;
  };

  const handleAddressEnriched = useCallback(
    (data = {}) => {
      const { ttb, payload } = data || {};
      if (ttb) setTtbData(ttb);

      // Auto Estimate Loan: default the toggle on and pull "Existing 1st"
      // balance/rate straight from this address's title-records loan
      // lookup, same as Seller. Only defaults it here, at the moment fresh
      // TTB data arrives — it never overrides a choice the user made
      // afterward via the toggle itself.
      const autoEstimate = hasAutoEstimatableLoanBalance(ttb);
      // eslint-disable-next-line no-console
      console.log("[autoEstimateLoan] handleAddressEnriched", {
        rawLoanBalance: ttb?.loanBalance,
        autoEstimate,
        balance: autoEstimate ? getAutoEstimatedLoanBalance(ttb) : null,
        rate: autoEstimate ? getAutoEstimatedLoanRate(ttb) : null,
      });
      setUseMortgageBalance(autoEstimate);
      if (autoEstimate) {
        setFormData((prev) => ({
          ...prev,
          loanInfoMeta: {
            ...(prev.loanInfoMeta || {}),
            autoEstimateLoan: true,
            loansToBePaidBalance1: getAutoEstimatedLoanBalance(ttb),
            loansToBePaidRate1: getAutoEstimatedLoanRate(ttb),
          },
        }));
      }

      const cid = payload?.companyid;
      if (cid !== undefined && cid !== null && String(cid).trim() !== "") {
        const n = Number(cid);
        setCompanyId(Number.isFinite(n) ? n : cid);
      }

      if (!payload) return;
      setFormData((prev) => {
        const updated = {
          ...prev,
          phase: "GeneralInformationSection",
          addressVerified: true,
          addressMeta: {
            ...(prev.addressMeta || {}),
            companyid: payload?.companyid ?? prev?.addressMeta?.companyid ?? "",
            address:
              payload?.formatted_address ?? prev?.addressMeta?.address ?? "",
            street_no: payload?.street_no ?? prev?.addressMeta?.street_no ?? "",
            street_name:
              payload?.route_long ??
              payload?.route ??
              prev?.addressMeta?.street_name ??
              "",
            city: payload?.city ?? prev?.addressMeta?.city ?? "",
            state:
              payload?.state_code ??
              payload?.state ??
              prev?.addressMeta?.state ??
              "",
            zip: payload?.zip ?? prev?.addressMeta?.zip ?? "",
            county: payload?.county_name ?? prev?.addressMeta?.county ?? "",
            closing_state:
              payload?.closing_state ?? prev?.addressMeta?.closing_state ?? "",
            closing_county:
              payload?.closing_county ??
              prev?.addressMeta?.closing_county ??
              "",
            countyid: payload?.countyid ?? prev?.addressMeta?.countyid ?? "",
            stateid: payload?.stateid ?? prev?.addressMeta?.stateid ?? "",
          },
        };
        return preserveAddressFields(prev, updated);
      });
    },
    [preserveAddressFields],
  );

  useEffect(() => {
    if (calcId) {
      const loadExisting = async () => {
        try {
          setIsEditLoading(true);
          const resp = await getRefinanceCalc(calcId);
          if (!resp) return;
          const existingData = resp || {};
          setShowManualSheet(true);
          setFormData(existingData);
          setTimeout(() => {
            setIsEditLoading(false);
          }, 500);
        } catch (error) { }
      };
      loadExisting();
    } else {
      setIsEditLoading(false);
    }
  }, [calcId]);

  useEffect(() => {
    if (isEditLoading) return;
    const daysInterestAmount = getDaysInterestAmount(
      parseCurrency(formData?.loanInfoMeta?.amount),
      parseCurrency(formData?.loanInfoMeta?.adjusted),
      formData?.loanInfoMeta?.interestRate1,
      formData?.prepaidsMeta?.daysInterest,
    );
    setFormData((prev) => ({
      ...prev,
      prepaidsMeta: {
        ...(prev.prepaidsMeta || {}),
        daysInterestAmount,
      },
    }));
    // The "Days Interest Amount" input is bound to react-hook-form's own
    // field.value (ControlledField), not to formData directly — without
    // this, the setFormData above is invisible on screen: the underlying
    // value is correct, but the input never re-renders to show it.
    if (typeof setValue === "function") {
      setValue("prepaidsMeta.daysInterestAmount", daysInterestAmount, {
        shouldDirty: true,
        shouldTouch: true,
      });
    }
  }, [formData?.prepaidsMeta?.daysInterest]);

  useEffect(() => {
    if (isEditLoading) return;
    const insAmount = getMonthInsurance(
      parseCurrency(formData?.salesPrice),
      formData?.prepaidsMeta?.rateThou,
      formData?.prepaidsMeta?.monthsIns,
      formData?.loanInfoMeta?.buyersLoanType,
    );
    setFormData((prev) => ({
      ...prev,
      prepaidsMeta: {
        ...(prev.prepaidsMeta || {}),
        insAmount,
      },
    }));
    // Same reasoning as daysInterestAmount above — keep RHF's own value in
    // sync so the "Amount" input actually reflects the recomputed figure.
    if (typeof setValue === "function") {
      setValue("prepaidsMeta.insAmount", insAmount, {
        shouldDirty: true,
        shouldTouch: true,
      });
    }
  }, [formData?.prepaidsMeta?.monthsIns, formData?.prepaidsMeta?.rateThou]);

  useEffect(() => {
    if (isEditLoading) return;
    const prepaidMonthTaxes = getPrepaidMonthTaxes(
      parseCurrency(formData?.salesPrice),
      formData?.prepaidsMeta?.rate,
      formData?.prepaidsMeta?.monthsTaxes,
    );
    setFormData((prev) => ({
      ...prev,
      prepaidsMeta: {
        ...(prev.prepaidsMeta || {}),
        taxesAmount: prepaidMonthTaxes,
      },
    }));
    // Same reasoning as daysInterestAmount above — keep RHF's own value in
    // sync so the "Amount" input actually reflects the recomputed figure.
    if (typeof setValue === "function") {
      setValue("prepaidsMeta.taxesAmount", prepaidMonthTaxes, {
        shouldDirty: true,
        shouldTouch: true,
      });
    }
  }, [formData?.prepaidsMeta?.monthsTaxes, formData?.prepaidsMeta?.rate]);

  useEffect(() => {
    if (isEditLoading) return;
    let prepaidMonthTaxes = "0.00";
    if (formData?.loanInfoMeta?.useForPrepaidCheck === true) {
      prepaidMonthTaxes = getAnnualTaxforPrepaid(formData);
    } else {
      prepaidMonthTaxes = getPrepaidMonthTaxes(
        parseCurrency(formData?.salesPrice),
        formData?.prepaidsMeta?.rate,
        formData?.prepaidsMeta?.monthsTaxes,
      );
    }
    setFormData((prev) => ({
      ...prev,
      prepaidsMeta: {
        ...(prev.prepaidsMeta || {}),
        taxesAmount: prepaidMonthTaxes,
      },
    }));
  }, [formData?.loanInfoMeta?.useForPrepaidCheck]);

  useEffect(() => {
    if (isEditLoading) return;
    const noPMI = formData?.loanInfoMeta?.noPMICheck;
    const dataTwoMonthsPMI = noPMI
      ? { rateMonthlyPMIAmount: "0.00", twoMonthsPMI: "0.00" }
      : getMonthlyPMIAmount(formData);
    setFormData((prev) => ({
      ...prev,
      paymentMeta: {
        ...(prev.paymentMeta || {}),
        rateMonthlyPMIAmount: dataTwoMonthsPMI.rateMonthlyPMIAmount,
      },
      prepaidsMeta: {
        ...(prev.prepaidsMeta || {}),
        twoMonthsPMI: dataTwoMonthsPMI.twoMonthsPMI,
      },
    }));
  }, [
    formData?.paymentMeta?.rateMonthlyPMI,
    formData?.loanInfoMeta?.noPMICheck,
  ]);

  useEffect(() => {
    if (isEditLoading) return;
    getTitleEscrowData(
      formData,
      templateData,
      formData?.loanInfoMeta?.buyersLoanType,
    );
  }, [formData?.loanInfoMeta?.noPMICheck]);

  useEffect(() => {
    if (isEditLoading) return;
    const taxAmount = Number(ttbData?.taxAmount || 0);
    const data1Proration = templateData?.data1Proration || {};
    if (taxAmount <= 0 || !Object.keys(data1Proration).length) return;
    setUseAnnualPropertyTax(useAnnualPropertyTax);
    const currentFormData =
      Object.keys(formData || {}).length > 0 ? formData : {};
    const estimatedTax = getBuyerEstimatedTax(
      currentFormData,
      templateData,
      "buyer",
    );
    if (!estimatedTax || isNaN(estimatedTax.estimatedTaxProrations)) return;
    setFormData((prev) => ({
      ...prev,
      paymentMeta: {
        ...(prev.paymentMeta || {}),
        estimatedTaxProrations: estimatedTax.estimatedTaxProrations,
      },
    }));
  }, [
    ttbData?.taxAmount,
    templateData?.data1Proration,
    formData?.loanInfoMeta?.annualPropertyTax,
    formData?.loanInfoMeta?.estimatedClosingDate,
  ]);

  useEffect(() => {
    if (!formData?.addressMeta?.stateid || !mainId) return;
    const fetchStateSettings = async () => {
      try {
        const normalizeArray = (data) =>
          Array.isArray(data) ? data : data ? [data] : [];
        const [dataProration, dataImpound] = await Promise.all([
          getStateSettings({
            stateid: formData.addressMeta.stateid,
            type: "buyer_proration",
            maincompanyid: mainId,
          }),
          getStateSettings({
            stateid: formData.addressMeta.stateid,
            type: "buyer_impound",
            maincompanyid: mainId,
          }),
        ]);
        const prorationList = normalizeArray(dataProration?.data);
        const impoundList = normalizeArray(dataImpound?.data);
        const prepaidTax = setPrepaidData(formData, impoundList[0]);
        setFormData((prev) => ({
          ...prev,
          prepaidsMeta: {
            ...(prev.prepaidsMeta || {}),
            monthsTaxes: prepaidTax.monthsTaxes,
          //  daysInterest: prepaidTax.daysInterest,
          },
        }));
        setTemplateData((prev) => ({
          ...(prev || {}),
          phase: "proration 2",
          data1Proration: prorationList[0],
          data2Proration: prorationList[1] || {},
          dataImpound: impoundList[0],
        }));
      } catch (error) { }
    };
    fetchStateSettings();
  }, [formData?.addressMeta?.stateid, mainId]);

  useEffect(() => {
    if (!formData?.addressMeta?.countyid || !mainId) return;
    const fetchCountySettings = async () => {
      try {
        const dataCountySetting = await getCountySettings({
          stateid: formData.addressMeta.stateid,
          countyid: formData.addressMeta.countyid,
          maincompanyid: mainId,
        });
        const dataArray = Array.isArray(dataCountySetting?.data)
          ? dataCountySetting.data
          : [];
        if (dataArray.length === 0) return;
        const firstCounty = dataArray[0];
        const dataCountyArr = {
          maxloanamount: firstCounty?.maxloanamount ?? "0.00",
          vamaxloanamount: firstCounty?.vamaxloanamount ?? "0.00",
        };
        setTemplateData((prev) => ({
          ...(prev || {}),
          phase: "county 2",
          dataCounty: dataCountyArr,
        }));
      } catch (error) {
        console.error("Failed to fetch county settings:", error);
      }
    };
    fetchCountySettings();
  }, [formData?.addressMeta?.countyid, mainId]);

  useEffect(() => {
    if (!mainId) return;
    const controller = new AbortController();

    const fetchNationalSettings = async () => {
      try {
        const res = await getNationalSettings(
          { maincompanyid: mainId },
          { signal: controller.signal },
        );
        if (controller.signal.aborted) return;
        const nationalData =
          Array.isArray(res?.data) && res.data.length > 0 ? res.data[0] : {};
        setFormData((prev) => ({
          ...prev,
          loanInfoMeta: {
            ...prev.loanInfoMeta,
            conventionalPercent:
              nationalData?.conventional_loans?.conventional_loan_defaults
                .resale_conventional_loan_ltv_percent,
          },
        }));
        setTemplateData((prev) => ({
          ...(prev || {}),
          phase: "national 2",
          dataNational: nationalData,
        }));
      } catch (error) {
        if (
          error?.code === "ERR_CANCELED" ||
          error?.name === "CanceledError"
        ) {
          return;
        }
      }
    };
    fetchNationalSettings();
    return () => controller.abort();
  }, [mainId]);

  useEffect(() => {
    if (isEditLoading) return;
    if (!formData?.addressMeta?.zip) return;
    getTitleEscrowData(
      formData,
      templateData,
      formData?.loanInfoMeta?.buyersLoanType,
    );
  }, [formData?.addressMeta?.zip]);

  useEffect(() => {
    if (isEditLoading) return;
    if (!formData?.loanInfoMeta?.buyerDownPayment) return;
    getTitleEscrowData(
      formData,
      templateData,
      formData?.loanInfoMeta?.buyersLoanType,
    );
  }, [formData?.loanInfoMeta?.buyerDownPayment]);

  useEffect(() => {
    if (isEditLoading) return;
    if (!formData.loanInfoMeta?.conventionalPercent) return;
    handleLoanAmount(formData, "firstLoan");
  }, [
    formData.loanInfoMeta?.conventionalPercent,
    formData.loanInfoMeta?.termsInYears1,
    formData.loanInfoMeta?.interestRate1,
  ]);

  useEffect(() => {
    if (isEditLoading) return;
    if (!formData.loanInfoMeta?.conventionalPercent2) return;
    handleLoanAmount(formData, "secondLoan");
  }, [
    formData.loanInfoMeta?.conventionalPercent2,
    formData.loanInfoMeta?.termsInYears2,
    formData.loanInfoMeta?.interestRate2,
  ]);

  useEffect(() => {
    if (isEditLoading) return;
    let fhaMipAmount = "0.00";
    let adjusted = "0.00";
    if (formData?.prepaidsMeta?.fhaMipCheck === true) {
      fhaMipAmount = formData?.prepaidsMeta?.FhaMipFin2;
      adjusted =
        parseCurrency(formData?.prepaidsMeta?.FhaMipFin) +
        (parseCurrency(formData.loanInfoMeta?.amount) || 0);
    } else {
      fhaMipAmount = formData?.prepaidsMeta?.FhaMipFin;
      adjusted = parseCurrency(formData.loanInfoMeta?.amount) || 0;
    }
    let interestRate = parseCurrency(formData.loanInfoMeta?.interestRate1);
    let daysInterest = parseCurrency(formData.prepaidsMeta?.daysInterest);
    let daysInterestAmount = getDaysInterestAmount(
      "0.00",
      adjusted,
      interestRate,
      daysInterest,
    );
    setFormData((prev) => ({
      ...prev,
      loanInfoMeta: {
        ...(prev.loanInfoMeta || {}),
        adjusted: String(adjusted),
      },
      prepaidsMeta: {
        ...(prev.prepaidsMeta || {}),
        fhaMipAmount: String(fhaMipAmount),
        daysInterestAmount: String(daysInterestAmount),
      },
    }));
  }, [formData?.prepaidsMeta?.fhaMipCheck]);

  useEffect(() => {
    if (isEditLoading) return;
    let vaffFinance = "0.00";
    let adjusted = "0.00";
    let vaffFinanceAmount = "0.00";
    if (formData?.prepaidsMeta?.vaffFinanceCheck === true) {
      vaffFinanceAmount = "0.00";
      vaffFinance = formData?.prepaidsMeta?.vaffFinanceFee;
      adjusted =
        parseCurrency(formData?.prepaidsMeta?.vaffFinanceFee) +
        (parseCurrency(formData.loanInfoMeta?.amount) || 0);
    } else {
      vaffFinanceAmount = formData?.prepaidsMeta?.vaffFinanceFee;
      vaffFinance = "0.00";
      adjusted = parseCurrency(formData.loanInfoMeta?.amount) || 0;
    }
    let interestRate = parseCurrency(formData.loanInfoMeta?.interestRate1);
    let daysInterest = parseCurrency(formData.prepaidsMeta?.daysInterest);
    let daysInterestAmount = getDaysInterestAmount(
      "0.00",
      adjusted,
      interestRate,
      daysInterest,
    );
    setFormData((prev) => ({
      ...prev,
      loanInfoMeta: {
        ...(prev.loanInfoMeta || {}),
        adjusted: String(adjusted),
      },
      prepaidsMeta: {
        ...(prev.prepaidsMeta || {}),
        vaffFinanceAmount: String(vaffFinanceAmount),
        vaffFinance: String(vaffFinance),
        daysInterestAmount: String(daysInterestAmount),
      },
    }));
  }, [formData?.prepaidsMeta?.vaffFinanceCheck]);

  useEffect(() => {
    if (isEditLoading) return;
    let adjusted = "0.00";
    let mipFinance = "0.00";
    let mipFinanceAmount = "0.00";
    if (formData?.prepaidsMeta?.mipFinanceCheck === true) {
      mipFinanceAmount = "0.00";
      mipFinance = formData?.prepaidsMeta?.UsdaMipFinance;
      adjusted =
        parseCurrency(formData?.prepaidsMeta?.UsdaMipFinance) +
        (parseCurrency(formData.loanInfoMeta?.amount) || 0);
    } else {
      mipFinanceAmount = formData?.prepaidsMeta?.UsdaMipFinance;
      mipFinance = formData?.prepaidsMeta?.UsdaMipFinance;
      adjusted = parseCurrency(formData.loanInfoMeta?.amount) || 0;
    }
    let interestRate = parseCurrency(formData.loanInfoMeta?.interestRate1);
    let daysInterest = parseCurrency(formData.prepaidsMeta?.daysInterest);
    let daysInterestAmount = getDaysInterestAmount(
      "0.00",
      adjusted,
      interestRate,
      daysInterest,
    );
    setFormData((prev) => ({
      ...prev,
      loanInfoMeta: {
        ...(prev.loanInfoMeta || {}),
        adjusted: String(adjusted),
      },
      prepaidsMeta: {
        ...(prev.prepaidsMeta || {}),
        mipFinanceAmount: String(mipFinanceAmount),
        mipFinance: String(mipFinance),
        daysInterestAmount: String(daysInterestAmount),
      },
    }));
  }, [formData?.prepaidsMeta?.mipFinanceCheck]);

  useEffect(() => {
    if (isEditLoading) return;
    let adjusted = "0.00";
    let mipFinance = "0.00";
    let mipFinanceAmount = "0.00";
    if (formData?.loanInfoMeta?.allowOwnerCarry1Check === true) {
    } else {
    }
    let interestRate = parseCurrency(formData.loanInfoMeta?.interestRate1);
    let daysInterest = parseCurrency(formData.prepaidsMeta?.daysInterest);
    let daysInterestAmount = getDaysInterestAmount(
      "0.00",
      adjusted,
      interestRate,
      daysInterest,
    );
    setFormData((prev) => ({
      ...prev,
      loanInfoMeta: {
        ...(prev.loanInfoMeta || {}),
        adjusted: String(adjusted),
      },
      prepaidsMeta: {
        ...(prev.prepaidsMeta || {}),
        mipFinanceAmount: String(mipFinanceAmount),
        mipFinance: String(mipFinance),
        daysInterestAmount: String(daysInterestAmount),
      },
    }));
  }, [formData?.loanInfoMeta?.allowOwnerCarry1Check]);

  useEffect(() => {
    if (isEditLoading) return;
    setFormData((prev) => ({
      ...prev,
      loanInfoMeta: {
        ...(prev.loanInfoMeta || {}),
        ...(useAnnualPropertyTax
          ? {
            annualPropertyTax:
              formData?.loanInfoMeta?.annualPropertyTaxAmount ?? 0,
          }
          : { annualPropertyTax: "0.00" }),
      },
    }));
  }, [useAnnualPropertyTax]);

  useEffect(() => {
    if (isEditLoading) return;
    const loan1 = parseCurrency(
      formData?.loanInfoMeta?.loansToBePaidBalance1 || 0,
    );
    const loan2 = parseCurrency(
      formData?.loanInfoMeta?.loansToBePaidBalance2 || 0,
    );
    const loan3 = parseCurrency(
      formData?.loanInfoMeta?.loansToBePaidBalance3 || 0,
    );
    const total = loan1 + loan2 + loan3;
    const totalFormatted = total.toFixed(2);
    setFormData((prev) => {
      const currentTotal = prev?.loanInfoMeta?.totalloansToBePaid;
      if (currentTotal === totalFormatted) return prev;
      return {
        ...prev,
        loanInfoMeta: {
          ...(prev.loanInfoMeta || {}),
          totalloansToBePaid: totalFormatted,
        },
      };
    });
  }, [
    formData?.loanInfoMeta?.loansToBePaidBalance1,
    formData?.loanInfoMeta?.loansToBePaidBalance2,
    formData?.loanInfoMeta?.loansToBePaidBalance3,
  ]);

  useEffect(() => {
    return () => { };
  }, []);

  useEffect(() => {
    if (isInitialLoad) return;
    if (Number(formData?.salePrice) < 50000) return;
    if (formData?.addressVerified === false) return;
    const discountData = calculateDiscount(formData, true);
    let conventionalPercent = "0.00";
    if (formData?.loanInfoMeta?.buyersLoanType === "Conventional") {
      conventionalPercent =
        templateData?.dataNational?.conventional_loans
          ?.conventional_loan_defaults?.resale_conventional_loan_ltv_percent;
    }
    const merged = {
      ...(formData || {}),
      phase: "computed",
      loanInfoMeta: {
        ...(formData?.loanInfoMeta || {}),
        conventionalPercent,
      },
      titleSettlementMeta: {
        ...(formData?.titleSettlementMeta || {}),
        discount: discountData.discountPercent,
        discountAmount: discountData.discountAmount,
      },
    };
    setFormData((prev) => preserveAddressFields(prev, merged));
    handleCalAmount(merged);
  }, [
    formData?.salesPrice,
    formData?.loanInfoMeta?.buyersLoanType,
    templateData,
  ]);

  useEffect(() => {
    if (!formData?.salesPrice && formData.salePrice < 50000) return;
    if (formData?.addressVerified === false) return;
    if (
      Array.isArray(formData?.closingCostsMeta) &&
      formData.closingCostsMeta.length > 0
    ) {
      setFormData((prev) => {
        const updated = computeClosingCostTotals(prev);
        const loanTypeChanged =
          updated.loanInfoMeta?.buyersLoanType !==
          prev.loanInfoMeta?.buyersLoanType;
        if (
          !loanTypeChanged &&
          JSON.stringify(updated.closingCostsMeta) ===
          JSON.stringify(prev.closingCostsMeta)
        ) {
          return prev;
        }
        return updated;
      });
    }
  }, [formData?.salesPrice, formData?.loanInfoMeta?.buyersLoanType]);

  const getTitleEscrowData = async (
    currentFormData,
    templateInfo,
    loanType,
  ) => {
    if (isEditLoading) return;
    const sp = Number(
      String(currentFormData.salesPrice || "").replace(/,/g, ""),
    );
    if (!sp || sp < 50000) return;
    if (currentFormData.addressVerified === false) return;
    try {
      let result = {};
      let amount = 0;
      if (loanType === "Conventional") {
        result = getAmountConventional(currentFormData);
        amount = result.amount;
      } else if (loanType === "FHA 203b") {
        result = getAmountFHA(currentFormData, templateInfo);
        amount = result.adjusted;
      } else if (loanType === "VA") {
        result = getAmountVA(currentFormData, templateInfo);
        amount = result.adjusted;
      }
      const payload = {
        closing_state: currentFormData?.addressMeta?.state,
        closing_city: currentFormData?.addressMeta?.city,
        closing_county: currentFormData?.addressMeta?.county,
        county: currentFormData?.addressMeta?.county,
        city: currentFormData?.addressMeta?.city,
        zip: currentFormData?.addressMeta?.zip,
        appraisedValue: currentFormData.salesPrice,
        newLoanAmount: amount,
        loanType: currentFormData?.loanInfoMeta?.buyersLoanType,
        estStlmtDate: currentFormData.loanInfoMeta.estimatedClosingDate,
        device: "web",
        calc: "Buyer",
        reissueyr: false,
        mrs_id: "Gf40rntTRu",
        prepared_for: currentFormData.preparedFor,
        liab_amount: "0.00",
        sim: true,
      };
      let dataTitleEscrow = await getTitleEscrowCosts(payload);
      if (dataTitleEscrow.status === "fail") {
        handleUpdateToaster({
          message:
            dataTitleEscrow.message || "Failed to fetch title & escrow data.",
          visible: true,
          type: "error",
        });
        return;
      }
      let escrowTitleData = resultOfTitleEscrow(dataTitleEscrow);
      let salesTaxRate = dataTitleEscrow?.data?.salesTaxRate;
      let state = currentFormData?.addressMeta?.state;
      let dataPrepaidPayment = await getPrepaidPaymentData(
        currentFormData,
        result,
        templateInfo,
      );
      let dataTwoMonthsPMI = await getTwoMonthPMI(
        currentFormData,
        templateInfo,
        result,
      );
      let latestClosingData = computeClosingCostTotals(currentFormData);
      let noPMI = currentFormData?.loanInfoMeta?.noPMICheck;
      dataTwoMonthsPMI = noPMI
        ? {
          rateMonthlyPMI: dataTwoMonthsPMI.rateMonthlyPMI,
          rateMonthlyPMIAmount: "0.00",
          twoMonthsPMI: "0.00",
        }
        : dataTwoMonthsPMI;
      setFormData((prev) => {
        let updated = structuredClone(prev);
        updated.addressVerified = prev.addressVerified;
        updated.phase = "getTitleEscrowData";
        updated.closingCostsMeta = latestClosingData.closingCostsMeta || [];
        if (
          Number(mainId) === 5 &&
          state === "WA" &&
          salesTaxRate !== undefined &&
          salesTaxRate !== null &&
          Number(salesTaxRate) !== 0
        ) {
          updated = applyWaSalesTaxClosingCosts(updated, {
            escrowSettlementAmount: escrowTitleData.escrowSettlementAmount,
            lendersTitleAmount: escrowTitleData.ownersTitleAmount,
            salesTaxRate,
            lenderRowIndex: "6",
            escrowTaxRowIndex: "7",
          });
        }
        updated.loanInfoMeta = {
          ...(updated.loanInfoMeta || {}),
          amount: result.amount,
          buyerDownPayment: result.downPayment,
          interestRate1: dataTitleEscrow?.data?.interestRate,
        };
        updated.main_company_name = main_company_name;
        updated.titleSettlementMeta = {
          ...(updated.titleSettlementMeta || {}),
          ...escrowTitleData,
          ...(salesTaxRate != null && Number(salesTaxRate) !== 0
            ? { salesTaxRate }
            : {}),
        };
        updated.prepaidsMeta = {
          ...(updated.prepaidsMeta || {}),
          taxesAmount: dataPrepaidPayment.prepaidMonthTaxes,
          insAmount: dataPrepaidPayment.monthInsurance,
          daysInterestAmount: dataPrepaidPayment.daysInterestAmount,
        };
        updated.paymentMeta = {
          ...(updated.paymentMeta || {}),
          principalAndInterest: dataPrepaidPayment.principalAndInterest,
          homeownerInsurance: dataPrepaidPayment.homeOwnerInsuranceRes,
          realEstateTaxes: dataPrepaidPayment.realEstateTaxes,
          pAndI2ndTDAmount: dataPrepaidPayment.pAndI2ndTDAmount,
          rateMonthlyPMI: dataTwoMonthsPMI.rateMonthlyPMI,
          rateMonthlyPMIAmount: dataTwoMonthsPMI.rateMonthlyPMIAmount,
        };

        if (loanType === "Conventional") {
          updated.prepaidsMeta = {
            ...(updated.prepaidsMeta || {}),
            twoMonthsPMI: dataTwoMonthsPMI.twoMonthsPMI,
            mipFinanceAmount: "0.00",
            fhaMipAmount: "0.00",
            vaffFinanceAmount: "0.00",
          };
          updated.loanInfoMeta = {
            ...(updated.loanInfoMeta || {}),
            amount2: result.amount2,
            adjusted: "0.00",
            allowOwnerCarry1Check: false,
            allowOwnerCarry2Check: false,
            conventionalPercent: result.ltv1,
          };
        } else if (loanType === "FHA 203b") {
          let fhaMipAmount = "0.00";
          let adjusted = "0.00";
          if (currentFormData?.prepaidsMeta?.fhaMipCheck === true) {
            fhaMipAmount = result.FhaMipFin2;
            adjusted =
              parseCurrency(result.FhaMipFin) +
              (parseCurrency(result.amount) || 0);
          } else {
            fhaMipAmount = result.FhaMipFin;
            adjusted = result.adjusted;
          }
          updated.loanInfoMeta = {
            ...(updated.loanInfoMeta || {}),
            adjusted: String(adjusted),
            allowOwnerCarry1Check: false,
            allowOwnerCarry2Check: false,
          };
          updated.prepaidsMeta = {
            ...(updated.prepaidsMeta || {}),
            twoMonthsPMI: "0.00",
            fhaMip: result.FhaMipFin1,
            fhaMipAmount: String(fhaMipAmount),
            FhaMipFin: result.FhaMipFin,
            FhaMipFin2: result.FhaMipFin2,
            daysInterestAmount: result.daysInterestAmount,
            mipFinanceAmount: "0.00",
            vaffFinanceAmount: "0.00",
          };
        } else if (loanType === "VA") {
          updated.loanInfoMeta = {
            ...(updated.loanInfoMeta || {}),
            adjusted: result.adjusted,
            allowOwnerCarry1Check: false,
            allowOwnerCarry2Check: false,
          };
          updated.prepaidsMeta = {
            ...(updated.prepaidsMeta || {}),
            twoMonthsPMI: "0.00",
            vaffFinance: result.VaFfFin,
            vaffFinanceFee: result.VaFfFin,
            vaffFinanceRate: result.vaffFinanceRate,
            mipFinanceAmount: "0.00",
            fhaMipAmount: "0.00",
          };
        } else if (loanType === "USDA") {
          updated.loanInfoMeta = {
            ...(updated.loanInfoMeta || {}),
            adjusted: result.adjusted,
            amount: result.amount,
            allowOwnerCarry1Check: false,
            allowOwnerCarry2Check: false,
          };
          updated.prepaidsMeta = {
            ...(updated.prepaidsMeta || {}),
            twoMonthsPMI: "0.00",
            UsdaMipFinance: result.UsdaMipFinance,
            mipFinance: result.UsdaMipFinance1,
            mipFinanceAmount: "0.00",
            fhaMipAmount: "0.00",
            vaffFinanceAmount: "0.00",
          };
        } else if (loanType === "Cash") {
          updated.loanInfoMeta = {
            ...(updated.loanInfoMeta || {}),
            adjusted: result.adjusted,
            amount: result.amount,
            downPayment: result.downPayment,
            allowOwnerCarry1Check: false,
            allowOwnerCarry2Check: false,
          };
          updated.paymentMeta = {
            ...(updated.paymentMeta || {}),
            principalAndInterest: result.principalAndInterest,
            realEstateTaxes: result.realEstateTaxes,
            pAndI2ndTDAmount: "0.00",
            rateMonthlyPMI: "0.00",
            rateMonthlyPMIAmount: "0.00",
            monthlyExpense1Amount: "0.00",
            monthlyExpense2Amount: "0.00",
          };
          updated.prepaidsMeta = {
            ...(updated.prepaidsMeta || {}),
            twoMonthsPMI: result.twoMonthsPMI,
            mipFinanceAmount: result.mipFinanceAmount,
            fhaMipAmount: result.fhaMipAmount,
            vaffFinanceAmount: result.vaffFinanceAmount,
            monthInsurance: result.monthInsurance,
            taxesAmount: result.taxesAmount,
            monthsTaxes: result.monthsTaxes,
            daysInterestAmount: result.daysInterestAmount,
            costsAmount: "0.00",
          };
        } else if (loanType === "Owner Carry") {
          updated.loanInfoMeta = {
            ...(updated.loanInfoMeta || {}),
            adjusted: "0.00",
            amount: result.amount,
            amount2: result.amount2,
            downPayment: result.downPayment,
            allowOwnerCarry1Check: true,
          };
          updated.paymentMeta = {
            ...(updated.paymentMeta || {}),
            realEstateTaxes: result.realEstateTaxes,
            pAndI2ndTDAmount: "0.00",
            rateMonthlyPMI: "0.00",
            rateMonthlyPMIAmount: "0.00",
          };
          updated.prepaidsMeta = {
            ...(updated.prepaidsMeta || {}),
            twoMonthsPMI: result.twoMonthsPMI,
            mipFinanceAmount: "0.00",
            fhaMipAmount: "0.00",
            vaffFinanceAmount: "0.00",
            taxesAmount: result.taxesAmount,
            monthsTaxes: result.monthsTaxes,
            daysInterestAmount: result.daysInterestAmount,
          };
        }
        const total = computeTotals(updated);
        updated.loanInfoMeta = {
          ...(updated.loanInfoMeta || {}),
          // Formatted to match how the Manual Sheet's own field handlers
          // store these (calculateTotalLoansToBePaid), so this field never
          // drifts into a raw-number vs. formatted-string mismatch.
          totalloansToBePaid: total.existingTotal.toFixed(2),
          daysInterest: total.existingLoanDaysInterest.toFixed(2),
        };
        updated.totalClosingMeta = {
          ...(updated.totalClosingMeta || {}),
          totalloansToBePaid: total.existingTotal,
          totalClosingCosts: total.totalClosingCosts,
          totalPrepaids: total.totalPrepaids,
          totalMonthlyPayment: total.totalMonthlyPayment,
          totalInvestment: total.totalInvestment,
          totalInvestmentText: total.totalInvestmentText,
        };
        return updated;
      });
    } finally {
      setIsInitialLoad(false);
    }
  };

  const handleCalAmount = useCallback(
    (snapshot) => {
      switch (snapshot.loanInfoMeta.buyersLoanType) {
        case "Conventional":
          getTitleEscrowData(snapshot, templateData, "Conventional");
          break;
        case "FHA 203b":
          getTitleEscrowData(snapshot, templateData, "FHA 203b");
          break;
        case "VA":
          getTitleEscrowData(snapshot, templateData, "VA");
          break;
        default:
          break;
      }
    },
    [templateData],
  );

  const handleCalculate = async () => {
    if (!formData?.salesPrice || Number(formData.salesPrice) === 0) {
      openModal({
        title: "Warnings",
        size: "smallmodel",
        content: (
          <div className="modelspace application-fe">
            <p className="model-desc-details notice-icon">
              You haven't entered a Sales Price.
              <br />
              Do you want to continue without a Sales Price?
            </p>
            <div className="frm-btn popup-2-btn">
              <button
                className="submit-btn btn-bg inpt-btn btn-discard"
                onClick={() => closeModal()}
              >
                Cancel
              </button>
              <button
                className="submit-btn btn-bg inpt-btn"
                onClick={() => {
                  closeModal();
                  runCalculation();
                }}
              >
                Continue
              </button>
            </div>
          </div>
        ),
      });
      return;
    }
    runCalculation();
  };

  const runCalculation = async () => {
    await getTitleEscrowData(formData, templateData, "Conventional");
    setTimeout(() => {
      openModal({
        title: "Refinance Net Sheet Results",
        size: "halffull",
        content: renderModal(1),
      });
    }, 50);
  };

  const handleLoanAmount = useCallback(
    (snapshot, loanType) => {
      setFormData((prev) => {
        const base =
          prev && Object.keys(prev).length
            ? prev
            : latestFormDataRef.current || snapshot || {};
        const result = getAmountConventional(base);
        const dataTwoMonthsPMI = getTwoMonthPMI(base, templateData, result);
        const dataPrepaidPayment =
          loanType === "firstLoan"
            ? getPrepaidPaymentData(base, result, templateData)
            : {};
        const dataPSecondTd =
          loanType === "secondLoan" ? getPSecondTd(base, result) : {};
        let updated = structuredClone(base || {});
        updated.addressVerified = base.addressVerified;
        updated.phase = "handleLoanAmount";
        updated.loanInfoMeta = {
          ...(updated.loanInfoMeta || {}),
          amount: result.amount,
          amount2: result.amount2,
          buyerDownPayment: result.downPayment,
          conventionalPercent: result.ltv1,
        };
        if (loanType === "firstLoan") {
          updated.prepaidsMeta = {
            ...(updated.prepaidsMeta || {}),
            taxesAmount: dataPrepaidPayment.prepaidMonthTaxes,
            insAmount: dataPrepaidPayment.monthInsurance,
            daysInterestAmount: dataPrepaidPayment.daysInterestAmount,
            twoMonthsPMI: dataTwoMonthsPMI.twoMonthsPMI,
          };
        } else if (loanType === "secondLoan") {
          updated.prepaidsMeta = {
            ...(updated.prepaidsMeta || {}),
            twoMonthsPMI: dataTwoMonthsPMI.twoMonthsPMI,
          };
        }
        if (loanType === "firstLoan") {
          updated.paymentMeta = {
            ...(updated.paymentMeta || {}),
            principalAndInterest: dataPrepaidPayment.principalAndInterest,
            homeownerInsurance: dataPrepaidPayment.homeOwnerInsuranceRes,
            realEstateTaxes: dataPrepaidPayment.realEstateTaxes,
            rateMonthlyPMI: dataTwoMonthsPMI.rateMonthlyPMI,
            rateMonthlyPMIAmount: dataTwoMonthsPMI.rateMonthlyPMIAmount,
          };
        } else if (loanType === "secondLoan") {
          updated.paymentMeta = {
            ...(updated.paymentMeta || {}),
            pAndI2ndTDAmount:
              dataPSecondTd?.pnintrate ||
              dataPSecondTd?.pAndI2ndTDAmount ||
              "0.00",
            rateMonthlyPMI: dataTwoMonthsPMI.rateMonthlyPMI,
            rateMonthlyPMIAmount: dataTwoMonthsPMI.rateMonthlyPMIAmount,
          };
        }
        const total = computeTotals(updated);
        updated.loanInfoMeta = {
          ...(updated.loanInfoMeta || {}),
          // Formatted to match how the Manual Sheet's own field handlers
          // store these (calculateTotalLoansToBePaid), so this field never
          // drifts into a raw-number vs. formatted-string mismatch.
          totalloansToBePaid: total.existingTotal.toFixed(2),
          daysInterest: total.existingLoanDaysInterest.toFixed(2),
        };
        updated.totalClosingMeta = {
          ...(updated.totalClosingMeta || {}),
          totalloansToBePaid: total.existingTotal,
          totalClosingCosts: total.totalClosingCosts,
          totalPrepaids: total.totalPrepaids,
          totalMonthlyPayment: total.totalMonthlyPayment,
          totalInvestment: total.totalInvestment,
          totalInvestmentText: total.totalInvestmentText,
        };
        return updated;
      });
    },
    [templateData],
  );

  const handleCancelModal = () => {
    closeModal();
    setTimeout(() => {
      openModal({
        title: "Refinance Net Sheet Results",
        size: "halffull",
        content: renderModal(1),
      });
    }, 50);
  };

  const handleCancelResultModal = () => {
    closeModal();
    setTimeout(() => {
      openModal({
        title: "What would you like to do next?",
        size: "smallmodel",
        content: renderModal(2),
      });
    }, 50);
  };

  const renderModal = (currentStep) => (
    <ResultsModal
      step={currentStep}
      setStep={(newStep) => {
        openModal({
          title: "What would you like to do next?",
          size: "smallmodel",
          content: renderModal(newStep),
        });
      }}
      closeModal={closeModal}
      onEditManual={() => {
        closeModal();
        setShowManualSheet(true);
      }}
      cancelModal={handleCancelModal}
      formData={latestFormDataRef.current}
      result={latestFormDataRef.current}
      formatCurrency={formatCurrency}
      handleSaveCalc={handleSaveBuyerCalc}
      handleDownloadPrintPDF={handleDownloadPrintPDF}
      getEffectiveFormData={getEffectiveFormData}
      cancelResultModal={handleCancelResultModal}
      showToaster={handleUpdateToaster}
    />
  );

  const resetSellerDefaults = () => {
    setFormData((prev) => ({
      ...prev,
      salesPrice: "0.00",
      phase: "resetSellerDefaults",
      loanInfoMeta: {
        ...(prev.loanInfoMeta || {}),
        annualPropertyTax: "0.00",
        mortgageAmount: "0.00",
        homeWarrantyAmount: "0.00",
      },
    }));
    setTtbData({});
    setIsCorrectProperty(false);
    setUseAnnualPropertyTax(useAnnualPropertyTax);
    setUseMortgageBalance(false);
    setUseHomeWarranty(false);
    setShowPreviewFromChild(false);
  };

  const handleNewCalc = () => {
    setFormData({});
    resetData();
    setTtbData({});
    setIsCorrectProperty(false);
    setUseAnnualPropertyTax(useAnnualPropertyTax);
    setUseMortgageBalance(false);
    setUseHomeWarranty(false);
    setShowPreviewFromChild(false);
    setIsInitialLoad(false);
    setShowManualSheet(false);
  };

  const resetData = () => {
    const buyerSections = templateData?.buyerSections;
    const titleSettlementMeta = {
      ownersTitleAmount: "0.00",
      escrowSettlementAmount: "0.00",
      lendersTitleAmount: "0.00",
      discount: "0.00",
      discountAmount: "0.00",
    };
    const addressMeta = {
      address: "",
      unitNumber: "",
      street_no: "",
      street_name: "",
      city: "",
      state: "",
      county: "",
      zip: "",
      closing_state: "",
      closing_county: "",
      closing_zip_code: "",
    };
    const finalFormData = {
      addressMeta,
      addressVerified: false,
      preparedBy: "",
      preparedFor: "New Client",
      salesPrice: "0.00",
      phase: "finalFormData",
      escrowPremiumChanged: false,
      loanInfoMeta: {
        estimatedClosingDate: "",
        buyerDownPayment: "0.00",
        buyersLoanType: "Conventional",
        annualPropertyTax: "0.00",
        assessorsTaxRate: "0.00",
        autoEstimateLoan: false,
        conventionalPercent: "80.00",
        conventionalPercent2: "0.00",
        interestRate1:
          buyerSections?.tenure?.find((it) => it.key === "todays_interest_rate")
            ?.value ?? "0.00",
        interestRate2: "0.00",
        termsInYears1:
          buyerSections?.tenure?.find(
            (it) => it.key === "terms_of_loans_in_years",
          )?.value ?? "0",
        termsInYears2: "0",
        days:
          buyerSections?.tenure?.find((it) => it.key === "number_of_days_per_month")
            ?.value ?? "0.00",
        amount: "0.00",
        amount2: "0.00",
        adjusted: "0.00",
        noPMICheck: false,
        showAPRCheck: false,
        allowOwnerCarry1Check: false,
        allowOwnerCarry2Check: false,
        addDueYear1: "0.00",
        addDueYear2: "0.00",
      },
      titleSettlementMeta,
      closingCostsMeta: buyerSections?.closingCosts,
      buyerClosingCostsMeta: buyerSections?.conventional,
      prepaidsMeta: {
        monthsTaxes: "0",
        rate:
          buyerSections?.insurance?.find(
            (it) => it.key === "tax_rate_per_year_per_100_of_sale_price",
          )?.value ?? "0.00",
        taxesAmount: "0.00",
        monthsIns:
          buyerSections?.insurance?.find(
            (it) =>
              it.key ===
              "homeowner_insurance_rate_per_year_per_1000_of_sale_price",
          )?.value ?? "0.00",
        rateThou:
          buyerSections?.insurance?.find(
            (it) => it.key === "number_of_months_insurance_prepaid",
          )?.value ?? "0.00",
        insAmount: "0.00",
        daysInterest:
          buyerSections?.tenure?.find((it) => it.key === "number_of_days_per_month")
            ?.value ?? "0.00",
        daysInterestAmount: "0.00",
        twoMonthsPMI: "0.00",
        fhaMip: "0.00",
        fhaMipCheck: true,
        fhaMipAmount: "0.00",
        FhaMipFin: "0.00",
        FhaMipFin2: "0.00",
        vaffFinance: "0.00",
        vaffFinanceCheck: true,
        vaffFinanceRate: "0.00",
        vaffFinanceAmount: "0.00",
        mipFinance: "0.00",
        mipFinanceCheck: true,
        mipFinanceAmount: "0.00",
        costs:
          buyerSections?.prepaid?.find((it) => it.key === "prepaid_other")
            ?.label ?? "Other",
        costsAmount:
          buyerSections?.prepaid?.find((it) => it.key === "prepaid_other")
            ?.value ?? "Other",
        totalPrepaidItems: "0.00",
      },
      paymentMeta: {
        principalAndInterest: "0.00",
        realEstateTaxes: "0.00",
        homeownerInsurance: "0.00",
        rateMonthlyPMI: "0.00",
        rateMonthlyPMIAmount: "0.00",
        pAndI2ndTDAmount: "0.00",
        monthlyExpense1:
          buyerSections?.payments?.find((it) => it.key === "other_4")?.label ??
          "Other",
        monthlyExpense2:
          buyerSections?.payments?.find((it) => it.key === "hoa_4")?.label ??
          "Other",
        monthlyExpense1Amount:
          buyerSections?.payments?.find((it) => it.key === "other_4")?.value ??
          "0.00",
        monthlyExpense2Amount:
          buyerSections?.payments?.find((it) => it.key === "hoa_4")?.value ??
          "0.00",
        totalMonthlyPayment: "0.00",
        estimatedTaxProrations: "0.00",
      },
      totalClosingMeta: {
        totalInvestment: "0.00",
        totalloansToBePaid: "0.00",
        totalClosingCosts: "0.00",
        totalPrepaids: "0.00",
        totalMonthlyPayment: "0.00",
      },
    };
    setFormData(finalFormData);
  };

  const checkValidSalesPrice = (dataOverride = formData) => {
    const formatted = String(dataOverride.salesPrice).replace(/,/g, "");
    const value = Number(formatted);
    if (!value) {
      handleUpdateToaster({
        message: "Failure! Please enter a Sales Price",
        visible: true,
        type: "error",
      });
    }
    return !isNaN(value) && value !== 0;
  };

  // Read from the ref, not `formData` directly — every one of these
  // handlers is called from ResultsModal via renderModal(), whose element
  // (and every prop/closure baked into it) is cached by ModalContext and
  // NOT recreated on every RefinanceNetSheet re-render. `formData` read
  // directly would still be whatever it was the moment the modal was first
  // opened; the ref is kept current by the effect above.
  //
  // Also converts the origination-fee row's percent to a dollar amount if
  // it's still raw — happens whenever ManualSheetSection never mounted this
  // session (e.g. the user reached this modal straight from the
  // address/Calculate flow), since that's the only other place this
  // conversion runs. Shared by every action that builds a payload from
  // form data (Save, Download/Print, Email) so there's exactly one place
  // this logic lives.
  const getEffectiveFormData = () => {
    const currentFormData = latestFormDataRef.current;
    return {
      ...currentFormData,
      buyerClosingCostsMeta: withOriginationFeeConverted(
        currentFormData?.buyerClosingCostsMeta,
        templateData?.buyerSections?.conventional,
        currentFormData?.salesPrice,
      ),
    };
  };

  const handleSaveBuyerCalc = async () => {
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const effectiveFormData = getEffectiveFormData();
      const buyerCalcId = effectiveFormData?.id;
      const payload = buildBuyerPayload({
        buyerformData: effectiveFormData,
        user,
        payload: {
          id: buyerCalcId,
          companyId: companyId,
          device: "web",
          calc: "Buyer",
          mrsid: effectiveFormData?.addressMeta?.mrsid,
          optionType: "save",
        },
      });
      if (!buyerCalcId) {
        const saveBuyerCalc = await saveRefinanceCalculator(payload);
        if (saveBuyerCalc) {
          const savedId = saveBuyerCalc?.calculator?.id;
          if (savedId) {
            setFormData((prev) => ({ ...(prev || {}), id: savedId }));
          }
          handleUpdateToaster({
            message: "Refinance Calculator saved successfully!",
            visible: true,
            type: "success",
          });
          closeModal();
        }
      } else {
        openModal({
          title: "Warnings",
          size: "mediumsizewidth",
          content: (
            <div className="modelspace application-fe">
              <p className="model-desc-details notice-icon">
                A saved file already exist with same file name, do you want to:
              </p>
              <div className="frm-btn popup-2-btn">
                <button
                  className="submit-btn btn-bg inpt-btn"
                  onClick={async () => {
                    await updateExisitingCalc(payload);
                  }}
                >
                  Overwrite Exisiting
                </button>
                <button
                  className="submit-btn btn-bg inpt-btn"
                  onClick={async () => {
                    await saveNewCalc();
                  }}
                >
                  Save as New File
                </button>
                <button
                  className="submit-btn btn-bg inpt-btn btn-discard"
                  onClick={() => closeModal()}
                >
                  Cancel
                </button>
              </div>
            </div>
          ),
        });
      }
    } catch (error) {
      console.log(error);
    }
  };

  const updateExisitingCalc = async (payload) => {
    try {
      const sellerCalc = await updateRefinanceCalculator(payload);
      if (sellerCalc) {
        closeModal();
        handleUpdateToaster({
          message: "Refinance Calculator updated successfully!",
          visible: true,
          type: "success",
        });
      }
    } catch (error) {
      console.warn("Error while updating the file", error);
    }
  };

  const saveNewCalc = async () => {
    try {
      setFormData((prev) => ({ ...(prev || {}), id: null }));
      handleSaveBuyerCalc();
    } catch (error) {
      console.warn("Error while Saving the file", error);
    }
  };

  const handleDownloadPrintPDF = async (type, buyerformData = []) => {
    try {
      const isValid = checkValidSalesPrice(latestFormDataRef.current);
      const user = JSON.parse(localStorage.getItem("user"));
      if (!isValid) return;
      const effectiveFormData = getEffectiveFormData();
      const payload = buildBuyerPayload({
        buyerformData: effectiveFormData,
        user,
        payload: {
          mrsid: effectiveFormData?.addressMeta?.mrsid,
          optionType:
            type === "download"
              ? "download"
              : type === "print"
                ? "printdetailedestimate"
                : "download",
        },
      });
      const data = await generatePdf({
        type: "refinance",
        data: { ...payload },
      });
      const blob = new Blob([data], { type: "application/pdf" });
      const blobUrl = window.URL.createObjectURL(blob);
      if (type === "download") {
        const link = document.createElement("a");
        link.href = blobUrl;
        let preparedFor = (payload?.preparedFor || "New_Client")
          .replace(/\s+/g, "_")
          .replace(/[^a-zA-Z0-9_]/g, "");
        let timestamp = Math.floor(Date.now() / 1000);
        link.download = `Print_for_${preparedFor}_${timestamp}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => {
          window.URL.revokeObjectURL(blobUrl);
        }, 5000);
      }
      if (type === "print") {
        const printWindow = window.open("", "_blank", "width=900,height=700");
        if (printWindow) {
          printWindow.document.write(`
            <html>
              <head><title>Print PDF</title></head>
              <body style="margin:0;">
                <iframe id="printFrame" src="${blobUrl}" style="width:100%; height:100%; border:none;"></iframe>
                <script>
                  const frame = document.getElementById('printFrame');
                  frame.onload = function () {
                    setTimeout(() => {
                      frame.contentWindow.focus();
                      frame.contentWindow.print();
                    }, 300);
                  };
                </script>
              </body>
            </html>
          `);
          printWindow.document.close();
        }
      }
    } catch (error) {
      console.warn("Error while downloading and print pdf", error);
    }
  };

  const handleUpdateToaster = ({ message, visible, type = "success" }) => {
    setToast({ message, visible, type });
    setTimeout(
      () => setToast({ message: "", visible: false, type: "success" }),
      5000,
    );
  };

  return (
    <div className="calculater-layout">
      <div className="calculator-header">
        <MainHeader />
      </div>
      <div>
        <div className="back-inner">
          {!showManualSheet && (
            <CommonHeader
              title={`Refinance Net Sheet`}
              subTitle={`Enter the property address and loan details to get started.`}
            />
          )}
        </div>
      </div>
      <div
        className={`calc-container ${showManualSheet === true ? "calc-page" : " double-height"
          }`}
      >
        <div className="clc-mid-contain">
          {!showManualSheet && (
            <AddressSearchPreview
              // Was "buyer" — a copy-paste artifact from Refinance's index.js
              // originally being derived from Buyer's. This mode value flows
              // into fetchCityStateForZip (see apiUtils.js), which is what
              // gates the existing-loan-balance estimate this whole Auto
              // Estimate Loan feature depends on — "buyer" never requested
              // one (a fresh purchase has no existing loan), so the address
              // enrichment response for Refinance never had ttb.loanBalance
              // populated at all. GeneralInformationSection's own separate
              // mode="buyer" prop (further down in ManualSheetSection.js)
              // is untouched and unrelated — confirmed it doesn't share
              // this value.
              mode="refinance"
              control={control}
              setValue={setValue}
              getValues={getValues}
              openModal={openModal}
              closeModal={closeModal}
              onEnriched={(data) => {
                const {
                  ttb,
                  payload,
                  showPreview,
                  showSiteUnit,
                  fromParcelSelection,
                } = data || {};
                const oldmrsid = formData?.addressMeta?.mrsid || "";
                const timestamp = Date.now();
                const enrichment = payload?.enrichment;
                const propertyUnavailable =
                  !ttb || Object.keys(ttb || {}).length === 0;
                if (propertyUnavailable) {
                  setFormData((prev) => ({
                    ...prev,
                    addressVerified: false,
                  }));
                  resetSellerDefaults();
                  handleUpdateToaster &&
                    handleUpdateToaster({
                      message:
                        "The address you entered is incorrect or the State/County is not supported.",
                      visible: true,
                      type: "error",
                    });
                  return;
                }
                if (ttb) setTtbData(ttb);
                // Auto Estimate Loan: same as handleAddressEnriched below —
                // default the toggle on and pull "Existing 1st"
                // balance/rate from this address's title-records loan
                // lookup, only at the moment fresh TTB data arrives.
                const autoEstimate = hasAutoEstimatableLoanBalance(ttb);
                // eslint-disable-next-line no-console
                console.log("[autoEstimateLoan] onEnriched (initial address entry)", {
                  rawLoanBalance: ttb?.loanBalance,
                  autoEstimate,
                  balance: autoEstimate ? getAutoEstimatedLoanBalance(ttb) : null,
                  rate: autoEstimate ? getAutoEstimatedLoanRate(ttb) : null,
                });
                setUseMortgageBalance(autoEstimate);
                if (payload?.companyid) {
                  setCompanyId(payload.companyid);
                }
                if (payload) {
                  const addDays = (date, days) => {
                    const d = new Date(date);
                    d.setDate(d.getDate() + days);
                    return d;
                  };
                  const toDateStr = (d) =>
                    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
                      2,
                      "0",
                    )}-${String(d.getDate()).padStart(2, "0")}`;
                  const defaultEst = toDateStr(addDays(new Date(), 30));
                  //  const [day] = defaultEst.split("-").map(Number);
                  setFormData((prev) => ({
                    ...prev,
                    phase: "AddressSearchPreview",
                    addressVerified: fromParcelSelection
                      ? true
                      : enrichment
                        ? true
                        : prev.addressVerified,
                    loanInfoMeta: {
                      ...(prev.loanInfoMeta || {}),
                      estimatedClosingDate: defaultEst,
                      //   days: day,
                      annualPropertyTaxAmount: ttb?.taxAmount,
                      ...(useAnnualPropertyTax
                        ? { annualPropertyTax: ttb?.taxAmount }
                        : { annualPropertyTax: "0.00" }),
                      ...(autoEstimate
                        ? {
                          autoEstimateLoan: true,
                          loansToBePaidBalance1:
                            getAutoEstimatedLoanBalance(ttb),
                          loansToBePaidRate1: getAutoEstimatedLoanRate(ttb),
                        }
                        : {}),
                    },
                    addressMeta: {
                      companyid: payload?.companyid || "",
                      address:
                        payload.formatted_address ||
                        prev?.addressMeta?.address ||
                        "",
                      street_no: payload.street_no,
                      street_name: payload.route_long,
                      closing_state:
                        payload?.closing_state || prev?.address?.state || "",
                      closing_county:
                        payload?.closing_county || prev?.address?.county || "",
                      closing_zip_code: "",
                      county:
                        payload?.county_name || prev?.address?.county || "",
                      city: payload?.city || prev?.address?.city || "",
                      state: payload?.state_code || "",
                      countyid:
                        payload?.countyid || prev?.address?.countyid || "",
                      stateid: payload?.stateid || prev?.address?.stateid || "",
                      zip: payload?.zip || prev?.address?.zip || "",
                      addressIncludesUnit:
                        showSiteUnit ||
                        prev?.address?.addressIncludesUnit ||
                        "",
                      unitNumber:
                        payload?.site_unit || prev?.address?.unitNumber || "",
                      mrsid: oldmrsid ? oldmrsid : `${timestamp}`,
                    },
                  }));
                }
                setShowPreviewFromChild(Boolean(showPreview));
                setIsInitialLoad(Boolean(showPreview));
                setIsCorrectProperty(true);
              }}
              onAddressEdited={(val) => {
                if (val && val.length > 3) {
                  setIsCorrectProperty(false);
                  setIsInitialLoad(false);
                  setUseAnnualPropertyTax(useAnnualPropertyTax);
                  // Not true: the old ttbData/loan balance belonged to
                  // whatever address was previously entered. Auto Estimate
                  // Loan re-enables itself once the new address is
                  // enriched and (if) a valid balance is found for it —
                  // see handleAddressEnriched / onEnriched above.
                  setUseMortgageBalance(false);
                  setUseHomeWarranty(false);
                }
              }}
            />
          )}
          {showPreviewFromChild && !showManualSheet && (
            <PropertyDetailsSection
              ttbData={ttbData}
              formatCurrency={formatCurrency}
              salesPriceError={salesPriceError}
              mortgageAmountError={mortgageAmountError}
              isCorrectProperty={isCorrectProperty}
              isInitialLoad={isInitialLoad}
              setIsCorrectProperty={setIsCorrectProperty}
              setIsInitialLoad={setIsInitialLoad}
              useAnnualPropertyTax={useAnnualPropertyTax}
              setUseAnnualPropertyTax={setUseAnnualPropertyTax}
              useMortgageBalance={useMortgageBalance}
              setUseMortgageBalance={setUseMortgageBalance}
              useHomeWarranty={useHomeWarranty}
              setUseHomeWarranty={setUseHomeWarranty}
              formData={formData}
              setFormData={setFormData}
              handleCurrencyBlur={handleCurrencyBlur}
              handleCalculate={handleCalculate}
            />
          )}
        </div>
        {showManualSheet && (
          <ManualSheetSection
            FloatingInput={FloatingInput}
            FloatingNumber={FloatingNumber}
            CustomSelect={CustomSelect}
            formatCurrency={formatCurrency}
            salesPriceError={salesPriceError}
            mortgageAmountError={mortgageAmountError}
            isCorrectProperty={isCorrectProperty}
            setIsCorrectProperty={setIsCorrectProperty}
            useAnnualPropertyTax={useAnnualPropertyTax}
            setUseAnnualPropertyTax={setUseAnnualPropertyTax}
            useMortgageBalance={useMortgageBalance}
            setUseMortgageBalance={setUseMortgageBalance}
            ttbData={ttbData}
            handleCurrencyBlur={handleCurrencyBlur}
            handleCalculate={handleCalculate}
            checkValidSalesPrice={checkValidSalesPrice}
            formData={formData}
            setFormData={setFormData}
            handleCalAmount={handleCalAmount}
            templateData={templateData?.buyerSections}
            buyerFormData={formData}
            onCloseManual={() => {
              handleNewCalc();
            }}
            handleSaveCalc={handleSaveBuyerCalc}
            handleDownloadPrintPDF={handleDownloadPrintPDF}
            showToaster={handleUpdateToaster}
            setCompanyId={setCompanyId}
            onAddressEnriched={handleAddressEnriched}
            preserveAddressFields={preserveAddressFields}
            handleClosingCostUpdate={handleClosingCostUpdate}
          />
        )}
        <Toast
          message={toast.message}
          type={toast.type}
          visible={toast.visible}
        />
      </div>
    </div>
  );
};

export default RefinanceNetSheet;
